import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2 } from "lucide-react";
import { useAuth } from "@/features/auth/contexts/AuthContext";
import { getDeviceId } from "@/shared/lib/device-id";
import { trackEvent } from "@/shared/lib/posthog";

import { cn } from "@/shared/lib/utils";
import { Spinner } from "@/components/ui/spinner";

const TOKEN_STORAGE_KEY = "ubik_session_token";
const PENDING_HANDOFF_KEY = "ubik_desktop_handoff_pending";
const DEFAULT_HANDOFF_PORT = "9001";
// Browsers only honour window.close() on tabs that scripts opened themselves
// (via window.open). The OAuth redirect / shell.openExternal flow lands the
// user on a tab they didn't open via JS, so close() silently no-ops in
// Chrome and Safari. We still attempt it (works in some Firefox configs and
// in PWA-installed contexts), and bump the delay so the user has time to
// read the message and dismiss the tab manually if Chrome refuses to close.
const AUTO_CLOSE_MS = 20_000;
// How long the brand quad spins before settling into the success checkmark.
const QUAD_SETTLE_MS = 600;

type Status = "preparing" | "waiting-auth" | "sent" | "no-token";

export default function DesktopAuth() {
  const { t } = useTranslation("pages");
  const { authData, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>("preparing");
  const [closing, setClosing] = useState(false);
  const [settled, setSettled] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState<number>(Math.round(AUTO_CLOSE_MS / 1000));

  useEffect(() => {
    if (loading) return;

    if (!authData) {
      sessionStorage.setItem(PENDING_HANDOFF_KEY, "1");
      setStatus("waiting-auth");
      navigate("/login", { replace: true });
      return;
    }

    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!token) {
      setStatus("no-token");
      return;
    }

    let cancelled = false;
    let closeTimer: ReturnType<typeof setTimeout> | undefined;

    // Loopback first — silent, no browser prompt. Deep-link is fallback only.
    const port =
      import.meta.env.VITE_UBIK_HANDOFF_PORT ?? DEFAULT_HANDOFF_PORT;

    const onSent = () => {
      setStatus("sent");
      closeTimer = setTimeout(() => {
        setClosing(true);
        // Best-effort close. The window.open(...,'_self') re-marries the tab
        // to a script-opened reference; some browsers then accept .close().
        // Most modern Chrome installs still refuse — we surface a manual
        // "close this tab" hint when that happens.
        try {
          window.open("", "_self");
          window.close();
        } catch { /* ignore */ }
      }, AUTO_CLOSE_MS);
    };

    // Hand the browser's device id to the desktop alongside the token, on
    // BOTH paths. The overlay persists it and advertises it back to the backend
    // (X-Ubik-Device-Id), which is what makes the meetings-page install check
    // per-device without a loopback probe.
    const deviceId = getDeviceId();

    fetch(`http://127.0.0.1:${port}/set-session`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, device_id: deviceId }),
      signal: AbortSignal.timeout(2000),
    })
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) throw new Error(`loopback ${res.status}`);
        // Handoff succeeded → the overlay now has the token + device id and
        // will register this device with the backend on its next authed call.
        onSent();
      })
      .catch(() => {
        if (cancelled) return;
        // Desktop not running (or loopback blocked) — try the deep link as a
        // last resort. macOS will prompt to launch Ubik; that's the right UX.
        try {
          window.location.href =
            `ubikmeeting://auth?token=${encodeURIComponent(token)}` +
            `&device_id=${encodeURIComponent(deviceId)}`;
        } catch { /* protocol not registered */ }
        onSent();
      });

    return () => {
      cancelled = true;
      if (closeTimer) clearTimeout(closeTimer);
    };
  }, [authData, loading, navigate]);

  // Fire an analytics event once when the terminal state is entered.
  useEffect(() => {
    if (status === "sent") {
      trackEvent("desktop_auth_completed", {});
    } else if (status === "no-token") {
      trackEvent("desktop_auth_no_token", {});
    }
  }, [status]);

  // Countdown for the close-tab hint. Only ticks while we're in the "sent"
  // state — pre-handoff there's nothing to count down to.
  useEffect(() => {
    if (status !== "sent") return;
    const interval = setInterval(() => {
      setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [status]);

  // Success-icon micro-transition: the brand quad spins briefly, then settles
  // into the checkmark. The handoff itself is already done by the time we're
  // in "sent" — the spin is deliberate, not a real pending state, so it's
  // short and terminates. Reset on leaving "sent" so a re-entry replays it.
  useEffect(() => {
    if (status !== "sent") {
      setSettled(false);
      return;
    }
    const t = setTimeout(() => setSettled(true), QUAD_SETTLE_MS);
    return () => clearTimeout(t);
  }, [status]);

  const isWaiting = loading || status === "preparing" || status === "waiting-auth";

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-6">
      <div className="w-full max-w-sm space-y-6 text-center">
        {/* Logo / Brand. ubik-logo.png (used here previously) is actually a
            renamed copy of the pre-rebrand favicon — same file as logo.png,
            predates the Solarpunk identity — not a distinct Ubik mark. Use
            the real theme-aware Ubik icon already established in
            AppSidebar.tsx instead. */}
        <div className="space-y-2">
          <img
            src="/ubik-icon-light.png"
            alt="Ubik"
            className="mx-auto h-12 w-12 rounded-md dark:hidden"
          />
          <img
            src="/ubik-icon-dark.png"
            alt="Ubik"
            className="mx-auto hidden h-12 w-12 rounded-md dark:block"
          />
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Ubik
          </h1>
        </div>

        {/* State card */}
        <div className="rounded-lg border border-border bg-card px-6 py-8 shadow-xs">
          {isWaiting && (
            <>
              <div className="flex h-6 items-center justify-center">
                <Spinner className="size-5" />
              </div>
              <p className="mt-4 text-sm font-medium text-foreground">
                {t("desktop_auth.connecting", "Connecting to desktop app")}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {t("desktop_auth.connecting_desc", "Sending your session over…")}
              </p>
            </>
          )}

          {status === "no-token" && (
            <>
              <p className="text-sm font-medium text-foreground">
                {t("desktop_auth.signin_required", "Sign in required")}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {t("desktop_auth.signin_required_desc", "You need an active Ubik session before we can hand off to the desktop app.")}
              </p>
              <button
                onClick={async () => {
                  // Log out the stale cookie-only session (no JWT in localStorage)
                  // so the login page can issue a fresh OAuth token for the handoff.
                  await logout();
                  navigate("/login?from=desktop", { replace: true });
                }}
                className="mt-4 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-xs transition-colors hover:bg-primary/90"
              >
                {t("desktop_auth.continue_signin", "Continue to sign in")}
              </button>
            </>
          )}

          {status === "sent" && (
            <>
              {/* Quad → checkmark. Both are stacked and cross-faded rather
                  than swapped, so the icon slot never reflows. Under
                  prefers-reduced-motion the quad is already static (see
                  .ubik-quad in index.css) and the fade collapses to a cut. */}
              <div className="relative mx-auto h-6 w-6">
                <div
                  className={cn(
                    "absolute inset-0 flex items-center justify-center transition-opacity duration-300 motion-reduce:transition-none",
                    settled ? "opacity-0" : "opacity-100",
                  )}
                >
                  <Spinner className="size-5" />
                </div>
                <CheckCircle2
                  className={cn(
                    "absolute inset-0 h-6 w-6 text-primary transition-opacity duration-300 motion-reduce:transition-none",
                    settled ? "opacity-100" : "opacity-0",
                  )}
                />
              </div>
              <p className="mt-4 text-sm font-medium text-foreground">
                {t("desktop_auth.all_set", "You're all set")}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {t("desktop_auth.all_set_desc", "Return to the desktop app to continue.")}
              </p>
              <p className="mt-4 text-xs text-muted-foreground/70">
                {closing
                  ? t("desktop_auth.closed_hint", "If this tab didn't close, you can close it now.")
                  : t("desktop_auth.closing_hint", "Closing in {{seconds}}s — or close this tab manually.", { seconds: secondsLeft })}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
